import { Input } from '@fluentui/react-components';
import {
  ChatThreadType,
  GuardianChatThreadItemResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInputAppearance } from '../../hooks';
import { useChatContextOptional } from '../ChatContext';
import './ChatList.scss';
import { ChatListEntry } from './ChatListEntry';

export interface ChatListProps {
  chatThreadItems?: GuardianChatThreadItemResponse[];
  onThreadItemClick?: (threadItem: GuardianChatThreadItemResponse) => void;
  selectedThreadItemId?: null | GuardianChatThreadItemResponse['id'];
  showSearch?: boolean;
}

export const ChatList: React.FC<ChatListProps> = (props) => {
  const { t } = useTranslation();
  const inputAppearance = useInputAppearance();
  const context = useChatContextOptional();

  const chatThreadItems = props.chatThreadItems ?? context?.threadItems ?? [];
  const selectedThreadItemId = props.selectedThreadItemId ?? context?.selectedThreadId ?? null;

  const onThreadItemClick = (threadItem: GuardianChatThreadItemResponse) => {
    if (threadItem.id === selectedThreadItemId && context?.deselectThread) {
      // Click on selected chat, deselects that thread
      context?.deselectThread();
    } else {
      const fn = props.onThreadItemClick ?? context?.selectThread;
      fn?.(threadItem);
    }
  };

  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredThreadItems = useMemo(() => {
    if (searchQuery.trim() === '') {
      return chatThreadItems;
    }

    const searchQueryLc = searchQuery.trim().toLocaleLowerCase();

    return chatThreadItems.filter(
      (item) =>
        item.topic.toLocaleLowerCase().includes(searchQueryLc) ||
        item.ward?.displayName.toLocaleLowerCase().includes(searchQueryLc),
    );
  }, [searchQuery, chatThreadItems]);

  return (
    <div className="ChatList flex-1 flex flex-col gap-3">
      {props.showSearch && (
        <div className="flex flex-row items-stretch">
          <Input
            appearance={inputAppearance}
            type="search"
            className="flex-1"
            placeholder={t('pc:chats:search_placeholder', { defaultValue: 'Chats durchsuchen...' })}
            value={searchQuery}
            onChange={(event, data) => setSearchQuery(data.value)}
          />
        </div>
      )}
      <ul className="flex flex-col gap-2">
        {filteredThreadItems.map((threadItem) => (
          <li key={threadItem.id}>
            <ChatListEntry
              threadId={threadItem.id}
              name={threadItem.topic ?? ''}
              type={threadItem.threadType ?? ChatThreadType.group}
              lastMessage={threadItem.lastMessage ?? undefined}
              isLocked={threadItem.isLocked}
              notificationCount={threadItem.unreadCount}
              onClick={() => onThreadItemClick?.(threadItem)}
              ward={threadItem.ward}
              selected={selectedThreadItemId === threadItem.id}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};
